import clsx from 'clsx'
import NextImage from 'next/legacy/image'
import type { FunctionComponent } from 'react'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import type { EvaluationInfoResult } from '../../data/EvaluationInfoFragment'
import type { ProductEvaluationResult } from '../../data/ProductEvaluationFragment'
import { ContentText } from '../content/ContentText'
import style from './EvaluationTemplate.module.sass'

type EvaluationTemplateProps = {
	evaluation: ProductEvaluationResult | undefined
	evaluationInfo: EvaluationInfoResult | undefined
}

export const EvaluationTemplate: FunctionComponent<EvaluationTemplateProps> = ({ evaluation, evaluationInfo }) => {
	const translate = useTranslate()

	return (
		<>
			{evaluation?.vetDieta1 === 'ANO' && evaluationInfo && (
				<div className={style.warningBox}>
					{evaluationInfo?.veterinaryDietIcon && (
						<div className={style.iconWrapper}>
							<NextImage
								src={evaluationInfo?.veterinaryDietIcon.url}
								alt={evaluationInfo?.veterinaryDietIcon.alt ?? translate('evaluationTemplate.veterinaryDiet.alt.image')}
								title={
									evaluationInfo?.veterinaryDietIcon.alt ?? translate('evaluationTemplate.veterinaryDiet.alt.image')
								}
								layout="fill"
								objectFit="contain"
							/>
						</div>
					)}
					<div className={style.tooltipDiet}>
						<h3 className={clsx(style.titleSmall, style.titleDiet)}>{evaluationInfo?.veterinaryDietTitle}</h3>

						<div className={style.tooltipContent}>
							<ContentText blocks={evaluationInfo.veterinaryDiet?.blocks ?? []} />
						</div>
					</div>
				</div>
			)}
			{evaluation?.vetDieta1 === 'NE' && (
				<>
					<h2 className={style.title}>
						<Translation tr="evaluationTemplate.veterinaryDiet.title" /> {evaluation?.vetDieta1}
					</h2>
					<p className={style.paragraph}>{evaluation?.vetDieta2}</p>
				</>
			)}
			<h2 className={style.title}>
				<Translation tr="evaluationTemplate.sizeAndAge.title" /> {evaluation?.vek} {evaluation?.velikostPlemen}
			</h2>
			<p className={style.paragraph}>
				<Translation tr="evaluationTemplate.sizeAndAge.paragraph" /> {evaluation?.vek} {evaluation?.velikostPlemen}
				<Translation tr="evaluationTemplate.sizeAndAge.ie" />{' '}
				<span className={style.boldText}>{evaluation?.hmotnostPlemene}</span>.
			</p>
			<h2 className={style.title}>
				<Translation tr="evaluationTemplate.granuleSize.title" /> {evaluation?.velikostGranuli}
			</h2>
			<p className={style.paragraph}>{evaluation?.velikostGranuliText}</p>
			<h2 className={style.title}>
				<Translation tr="evaluationTemplate.reproAppropriate.title" /> {evaluation?.plodnostKastraceNadpis}
			</h2>
			<p className={style.paragraph}>
				<Translation tr="evaluationTemplate.fromTitleFollows" /> {evaluation?.plodnostKastraceText}
			</p>
			<h2 className={style.title}>
				<Translation tr="evaluationTemplate.idealActivity.title" /> {evaluation?.doporucenaAktivitaDleMe}
			</h2>
			<p className={style.paragraph}>
				<Translation tr="evaluationTemplate.fromTitleFollows.manufacturerRecommends" />{' '}
				<span className={style.boldText}>{evaluation?.aktivita}</span>.
			</p>
			<p
				className={clsx(
					style.paragraph,
					evaluation?.vypocitanaMeVsMeVyrobce?.startsWith('PLUS') ? style.greenColor : style.redColor,
					style.boldText,
				)}
			>
				{evaluation?.vypocitanaMeVsMeVyrobce}{' '}
				{evaluation?.vypocitanaMeVsMeVyrobce2 && evaluation?.vypocitanaMeVsMeVyrobce2}
			</p>
			<p className={clsx(style.paragraph, style.italicText)}>
				<Translation tr="evaluationTemplate.note.first" />{' '}
				<span className={style.boldText}>
					<Translation tr="evaluationTemplate.note.second.bold" />
				</span>{' '}
				<Translation tr="evaluationTemplate.note.third" />{' '}
				<span className={style.boldText}>
					<Translation tr="evaluationTemplate.note.fourth.bold" />
				</span>
				.
			</p>
			<h2 className={style.title}>
				<Translation tr="evaluationTemplate.composition.meatAsFirst" /> {evaluation?.masoAnoNe}
			</h2>
			<p className={style.paragraph}>{evaluation?.masoNaPrvnimMisteText}</p>
			<h2 className={style.title}>
				<Translation tr="evaluationTemplate.flavors.title" />{' '}
				<span className={style.lowerCaseText}>{evaluation?.zdrojeZivocisnychBilkovinDle1Vyskytu}</span>
			</h2>
			<p className={style.paragraph}>{evaluation?.prichuteSeznam}</p>
			<h2 className={style.title}>
				<Translation tr="evaluationTemplate.eliminationDiet.title" /> {evaluation?.eliminacniDietaAnoNe}
			</h2>
			<p className={style.paragraph}>{evaluation?.eliminacniDietaTextInterpunkce}</p>
			<h2 className={style.title}>
				<Translation tr="evaluationTemplate.allergens.title" /> {evaluation?.alergenyAnoNe}
			</h2>
			{evaluation?.alergenyZadneNeobsahuje && <p className={style.paragraph}>{evaluation?.alergenyZadneNeobsahuje}</p>}
			{evaluation?.alergenyObsahuje && (
				<p className={clsx(style.paragraph, style.redColor)}>{evaluation?.alergenyObsahuje}</p>
			)}
			{evaluation?.alergenyNejakeNeobsahuje && (
				<p className={style.paragraph}>{evaluation?.alergenyNejakeNeobsahuje}</p>
			)}
			<h2 className={style.title}>{evaluation?.stravitelnostNadpis}</h2>
			<p className={style.paragraph}>
				<Translation tr="evaluationTemplate.digestibility.paragraph" />
			</p>
			{evaluation?.stravitelnostChybiText ? (
				<p className={style.paragraph}>{evaluation?.stravitelnostChybiText}</p>
			) : (
				<>
					<p className={style.paragraph}>
						<Translation tr="evaluationTemplate.digestibility.standards" />{' '}
						<span className={style.boldText}>
							<Translation tr="evaluationTemplate.digestibility.coeficient" /> {evaluation?.stravitelnostProcenta}{' '}
							<Translation tr="evaluationTemplate.digestibility.unit" />
						</span>
						. <Translation tr="evaluationTemplate.digestibility.usuals" />{' '}
						<span className={style.boldText}>
							<Translation tr="evaluationTemplate.digestibility.word" /> {evaluation?.stravitelnostSlovne}
						</span>
						.
					</p>
				</>
			)}
			<h2 className={style.title}>
				<Translation tr="evaluationTemplate.composition.title" />
			</h2>
			{evaluation?.prospesneDruhyInterpunkce && (
				<p className={style.paragraph}>{evaluation?.prospesneDruhyInterpunkce}</p>
			)}
			{evaluation?.nevhodneDruhy && <p className={style.paragraph}>{evaluation?.nevhodneDruhy}</p>}
			{(evaluation?.kladneUcinkyCeleTelo ||
				evaluation?.kladneUcinkyHlava ||
				evaluation?.kladneUcinkyHrudnik ||
				evaluation?.kladneUcinkyBricho ||
				evaluation?.kladneUcinkySpodniCast) && (
				<>
					<h2 className={style.title}>
						<Translation tr="evaluationTemplate.positiveEffects.title" />
					</h2>
					<ul className={style.list}>
						{evaluation?.kladneUcinkyCeleTelo && <li>{evaluation?.kladneUcinkyCeleTelo}</li>}
						{evaluation?.kladneUcinkyHlava && <li>{evaluation?.kladneUcinkyHlava}</li>}
						{evaluation?.kladneUcinkyHrudnik && <li>{evaluation?.kladneUcinkyHrudnik}</li>}
						{evaluation?.kladneUcinkyBricho && <li>{evaluation?.kladneUcinkyBricho}</li>}
						{evaluation?.kladneUcinkySpodniCast && <li>{evaluation?.kladneUcinkySpodniCast}</li>}
					</ul>
				</>
			)}
			{(evaluation?.zaporneUcinkyCeleTelo ||
				evaluation?.zaporneUcinkyHlava ||
				evaluation?.zaporneUcinkyHrudnik ||
				evaluation?.zaporneUcinkyBricho ||
				evaluation?.zaporneUcinkySpodniCast) && (
				<>
					<h2 className={style.title}>
						<Translation tr="evaluationTemplate.negativeEffects.title" />
					</h2>
					<ul className={clsx(style.list, style.listRedColor)}>
						{evaluation?.zaporneUcinkyCeleTelo && (
							<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.zaporneUcinkyCeleTelo}</li>
						)}
						{evaluation?.zaporneUcinkyHlava && (
							<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.zaporneUcinkyHlava}</li>
						)}
						{evaluation?.zaporneUcinkyHrudnik && (
							<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.zaporneUcinkyHrudnik}</li>
						)}
						{evaluation?.zaporneUcinkyBricho && (
							<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.zaporneUcinkyBricho}</li>
						)}
						{evaluation?.zaporneUcinkySpodniCast && (
							<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.zaporneUcinkySpodniCast}</li>
						)}
					</ul>
				</>
			)}
			<h2 className={style.title}>
				<Translation tr="evaluationTemplate.recommendedAmounts.title" />
			</h2>
			<p className={style.paragraph}>
				<Translation tr="evaluationTemplate.recommendedAmounts.granules" />{' '}
				<span className={style.boldText}>{evaluation?.nazev}</span>{' '}
				<Translation tr="evaluationTemplate.recommendedAmounts.byStandards" />
			</p>
			{(evaluation?.dsneuvedeno || evaluation?.dsmalo || evaluation?.dsprilis || evaluation?.dsok) && (
				<ul className={style.list}>
					{evaluation?.dsneuvedeno && (
						<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.dsneuvedeno}</li>
					)}
					{evaluation?.dsmalo && <li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.dsmalo}</li>}
					{evaluation?.dsprilis && <li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.dsprilis}</li>}
					{evaluation?.dsok && <li>{evaluation?.dsok}</li>}
				</ul>
			)}
			{(evaluation?.d95malo ||
				evaluation?.d95prilis ||
				evaluation?.d95ok ||
				evaluation?.d110malo ||
				evaluation?.d110prilis ||
				evaluation?.d110ok ||
				evaluation?.sdo14reproMalo ||
				evaluation?.sdo14reproPrilis ||
				evaluation?.sdo14reproOk ||
				evaluation?.sdo15malo ||
				evaluation?.sdo15prilis ||
				evaluation?.sdo15ok ||
				evaluation?.s15malo ||
				evaluation?.s15prilis ||
				evaluation?.s15ok) && (
				<>
					<p className={clsx(style.paragraph, style.paragraphMarginTop)}>
						<Translation tr="evaluationTemplate.recommendedAmounts.includes" />
					</p>
					{(evaluation?.d95malo || evaluation?.d95prilis || evaluation?.d95ok) && (
						<>
							<h3 className={style.titleSmall}>
								<Translation tr="evaluationTemplate.recommendedAmounts.adults.lowActivity" />
							</h3>
							<ul className={clsx(style.list, style.listSmall)}>
								{evaluation?.d95malo && (
									<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.d95malo}</li>
								)}
								{evaluation?.d95prilis && (
									<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.d95prilis}</li>
								)}
								{evaluation?.d95ok && <li>{evaluation?.d95ok}</li>}
							</ul>
						</>
					)}
					{(evaluation?.d110malo || evaluation?.d110prilis || evaluation?.d110ok) && (
						<>
							<h3 className={style.titleSmall}>
								<Translation tr="evaluationTemplate.recommendedAmounts.adults.midHighActivity" />
							</h3>
							<ul className={clsx(style.list, style.listSmall)}>
								{evaluation?.d110malo && (
									<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.d110malo}</li>
								)}
								{evaluation?.d110prilis && (
									<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.d110prilis}</li>
								)}
								{evaluation?.d110ok && <li>{evaluation?.d110ok}</li>}
							</ul>
						</>
					)}
					{(evaluation?.sdo14reproMalo || evaluation?.sdo14reproPrilis || evaluation?.sdo14reproOk) && (
						<>
							<h3 className={style.titleSmall}>
								<Translation tr="evaluationTemplate.recommendedAmounts.newBornRepro" />
							</h3>
							<ul className={clsx(style.list, style.listSmall)}>
								{evaluation?.sdo14reproMalo && (
									<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.sdo14reproMalo}</li>
								)}
								{evaluation?.sdo14reproPrilis && (
									<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.sdo14reproPrilis}</li>
								)}
								{evaluation?.sdo14reproOk && <li>{evaluation?.sdo14reproOk}</li>}
							</ul>
						</>
					)}
					{(evaluation?.sdo15malo || evaluation?.sdo15prilis || evaluation?.sdo15ok) && (
						<>
							<h3 className={style.titleSmall}>
								<Translation tr="evaluationTemplate.recommendedAmounts.puppies.small" />
							</h3>{' '}
							<ul className={clsx(style.list, style.listSmall)}>
								{evaluation?.sdo15malo && (
									<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.sdo15malo}</li>
								)}
								{evaluation?.sdo15prilis && (
									<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.sdo15prilis}</li>
								)}
								{evaluation?.sdo15ok && <li>{evaluation?.sdo15ok}</li>}
							</ul>
						</>
					)}
					{(evaluation?.s15malo || evaluation?.s15prilis || evaluation?.s15ok) && (
						<>
							<h3 className={style.titleSmall}>
								<Translation tr="evaluationTemplate.recommendedAmounts.puppies.big" />
							</h3>{' '}
							<ul className={clsx(style.list, style.listSmall)}>
								{evaluation?.s15malo && (
									<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.s15malo}</li>
								)}
								{evaluation?.s15prilis && (
									<li className={clsx(style.liRedColor, style.redColor)}>{evaluation?.s15prilis}</li>
								)}
								{evaluation?.s15ok && <li>{evaluation?.s15ok}</li>}
							</ul>
						</>
					)}
				</>
			)}
			{evaluationInfo?.disclaimer && (
				<div className={style.disclaimer}>
					<ContentText blocks={evaluationInfo.disclaimer?.blocks ?? []} />
				</div>
			)}
		</>
	)
}
